/**
 * MojPradCard - Displays Mój Prąd 7.0 subsidy estimate.
 *
 * MP 7.0 (Q1 2026): for existing PV owners adding battery storage.
 * MP 6.0 is expired (Aug 2025). Only MP_7_0 | INELIGIBLE are valid values.
 *
 * Returns null when subsidyEstimate is undefined.
 */

import { Card, Text, Badge, Stack, Group, ThemeIcon, Alert } from '@mantine/core';
import { IconSun, IconAlertCircle, IconInfoCircle } from '@tabler/icons-react';
import type { MojPradSubsidyResult } from '../../types';

export interface MojPradCardProps {
  subsidyEstimate?: MojPradSubsidyResult;
}

function formatPln(amount: number): string {
  // Use Intl.NumberFormat for reliable locale formatting
  return new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 0 }).format(Math.round(amount));
}

export function MojPradCard({ subsidyEstimate }: MojPradCardProps) {
  if (!subsidyEstimate) {
    return null;
  }

  const isIneligible = subsidyEstimate.programVersion === 'INELIGIBLE';

  if (isIneligible) {
    return (
      <Card withBorder radius="md" p="lg">
        <Stack gap="md">
          <Group gap="xs">
            <ThemeIcon color="gray" variant="light" size="lg" radius="xl">
              <IconSun size={20} />
            </ThemeIcon>
            <Text fw={600} size="lg">Mój Prąd</Text>
            <Badge color="gray" variant="light" size="md">NIEUPRAWNIENY</Badge>
          </Group>
          {subsidyEstimate.reason && (
            <Alert icon={<IconAlertCircle size={16} />} color="orange" variant="light">
              {subsidyEstimate.reason}
            </Alert>
          )}
        </Stack>
      </Card>
    );
  }

  return (
    <Card withBorder radius="md" p="lg" style={{ borderColor: 'var(--mantine-color-teal-4)' }}>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <ThemeIcon color="teal" variant="light" size="lg" radius="xl">
              <IconSun size={20} />
            </ThemeIcon>
            <Text fw={600} size="lg">Mój Prąd 7.0</Text>
          </Group>
          <Badge color="teal" size="lg" variant="filled">
            Uprawniony
          </Badge>
        </Group>

        <Group justify="space-between">
          <Text size="sm" c="dimmed">Szacowana dotacja</Text>
          <Text fw={700} size="xl" c="teal" data-testid="eligible-amount">
            PLN {formatPln(subsidyEstimate.eligibleAmount)}
          </Text>
        </Group>

        {subsidyEstimate.details.pvBaseAmount > 0 && (
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Podstawa PV</Text>
            <Text fw={500} data-testid="pv-base-amount">
              PLN {formatPln(subsidyEstimate.details.pvBaseAmount)}
            </Text>
          </Group>
        )}

        {subsidyEstimate.details.storageAdditional > 0 && (
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Dodatek za magazyn energii</Text>
            <Text fw={500} data-testid="storage-additional">
              +PLN {formatPln(subsidyEstimate.details.storageAdditional)}
            </Text>
          </Group>
        )}

        <Alert icon={<IconInfoCircle size={16} />} color="teal" variant="light">
          <Text size="xs" data-testid="program-description">
            Program Mój Prąd 7.0 (od Q1 2026) przeznaczony jest dla istniejących prosumentów
            rozbudowujących instalację o magazyn energii. Dofinansowanie obejmuje zakup i montaż
            baterii.
          </Text>
        </Alert>
      </Stack>
    </Card>
  );
}
